import { DestroyRef, Directive, effect, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AnimationItem } from 'lottie-web';
import { LottieComponent } from 'ngx-lottie';
import { VolumeLevelService } from '@libs/shared-services';

// The equalizer json is a 90-frame loop of three 30-frame "pulses" (baseline->peak->baseline at frames 0-30, 30-60, 60-90);
// each pulse animates a different subset of bars with a different total energy, surfacing the later-frame "tails" (the 45 & 75 peaks).
// Resting behaviour freeze-frames onto the first pulse (0-RAMP_FRAMES) as a volume meter; a volume onset "bounces" one full pulse,
// louder onsets picking higher-energy pulses.
@Directive({
  selector: '[libVolumeReactiveLottie]',
  standalone: true,
})
export class VolumeReactiveLottieDirective {
  private readonly volumeLevelService = inject(VolumeLevelService);
  private readonly lottieComponent = inject(LottieComponent);
  private readonly destroyRef = inject(DestroyRef);

  // Empirical estimates from mic-level-indicator/audio-visualizer calibration - needs real-mic tuning during visual QA.
  private readonly RAMP_FRAMES = 45;
  private readonly VOLUME_SMOOTHING = 0.25;
  private readonly MAX_EXPECTED_VOLUME = 25;

  // Onset "bounce": when the raw level jumps ONSET_DELTA above the smoothed baseline (and clears a noise floor), play one pulse.
  private readonly PULSE_FRAMES = 30;
  private readonly PULSE_COUNT = 3;
  private readonly ONSET_DELTA = 4;
  private readonly ONSET_FLOOR = 6;
  // Excess span (above ONSET_DELTA) over which spike strength ramps 0->1 and thus selects progressively higher-energy pulses.
  private readonly SPIKE_RANGE = 12;
  // Pulse indices (start = index * PULSE_FRAMES) ordered by ascending bar-movement energy: [60-90] < [0-30] < [30-60].
  private readonly PULSE_ORDER_BY_ENERGY = [2, 0, 1];

  private anim: AnimationItem | null = null;
  private smoothedVolume = 0;
  private isBouncing = false;

  constructor() {
    this.lottieComponent.animationCreated.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((anim) => {
      this.anim = anim;
      this.smoothedVolume = 0;
      this.isBouncing = false;
      anim.addEventListener('complete', () => {
        this.isBouncing = false;
      });
      anim.goToAndStop(0, true);
    });

    effect(() => {
      const raw = this.volumeLevelService.mediaVolumeLevel();
      const previousSmoothed = this.smoothedVolume;
      this.smoothedVolume = previousSmoothed * (1 - this.VOLUME_SMOOTHING) + raw * this.VOLUME_SMOOTHING;

      if (!this.anim) {
        return;
      }

      const excess = raw - previousSmoothed;
      const isOnset = excess > this.ONSET_DELTA && raw > this.ONSET_FLOOR;
      if (isOnset && !this.isBouncing) {
        this.playBounce(excess);
        return;
      }

      // While a pulse is playing, let it run out untouched so it reaches the tail frames.
      if (this.isBouncing) {
        return;
      }

      const normalized = Math.min(1, Math.max(0, this.smoothedVolume / this.MAX_EXPECTED_VOLUME));
      const frame = Math.round(normalized * this.RAMP_FRAMES);
      this.anim.goToAndStop(frame, true);
    });
  }

  private playBounce(excess: number): void {
    if (!this.anim) {
      return;
    }

    this.isBouncing = true;
    // Map spike strength (excess above the onset threshold) onto a progressively higher-energy pulse, incl. the post-45 tails.
    const strength = Math.min(1, Math.max(0, (excess - this.ONSET_DELTA) / this.SPIKE_RANGE));
    const bucket = Math.min(this.PULSE_COUNT - 1, Math.floor(strength * this.PULSE_COUNT));
    const start = this.PULSE_ORDER_BY_ENERGY[bucket] * this.PULSE_FRAMES;
    this.anim.playSegments([start, start + this.PULSE_FRAMES], true);
  }
}
